import {
  Labeled,
  SimpleShowLayout,
  useGetList,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { TherapistRecordingView } from '../therapistRecordings/TherapistRecordingView';
import { ForceAggregationButton } from '../therapistRecordings/ForceAggregationButton';
import { RecordingOperationHistory } from '../therapistRecordings/RecordingOperationHistory';
import { CopiableField } from '@/components/CopiableIdField';
import { adminRpcClient } from '@/providers/backendClient';

export const TherapistHomeworkCommandPanel = () => {
  const sessionNote = useRecordContext();

  const [isUpdating, setIsUpdating] = useState(false);

  const {
    data: recordings,
    isLoading,
    refetch,
  } = useGetList('therapistRecordings', {
    filter: { clientRecordingId: sessionNote.clientRecordingId },
    sort: { field: 'fileIndex', order: 'DESC' },
    pagination: { page: 1, perPage: 1000 },
  });

  const aggregateAvailable = useMemo(
    () =>
      recordings?.some(
        rec => rec.status === 'transcribed' && rec.transcript,
      ),
    [recordings],
  );

  const forceProcessAvailable = useMemo(
    () => !!sessionNote?.transcript,
    [sessionNote],
  );

  const actionsAvailable =
    aggregateAvailable || forceProcessAvailable;

  const refresh = useRefresh();

  const onForceProcess = useCallback(async () => {
    setIsUpdating(true);

    await adminRpcClient('PUBLIC_ADMIN_therapistAsset_process', [
      'therapistSessionNotes',
      sessionNote.id,
    ]).catch((err: any) => {
      alert('Error processing session notes: ' + err.message);
    });

    setIsUpdating(false);

    refresh();
  }, [sessionNote, refresh]);

  const sessionButtonText = useMemo(
    () =>
      sessionNote.doneAt ? 'Force Re-Process' : 'Force Processing',
    [sessionNote.doneAt],
  );

  const onReloadRecordings = useCallback(() => refetch(), [refetch]);

  const lastUpdatedAt = useMemo(() => {
    const [firstRecording] = recordings || [];

    return (
      sessionNote.lastUpdatedAt ||
      firstRecording?.lastUpdatedAt ||
      firstRecording?.createdAt
    );
  }, [sessionNote, recordings]);

  return (
    <SimpleShowLayout record={sessionNote}>
      <h2>Metadata</h2>
      <Box display="flex">
        <Labeled label="Note Id">
          <CopiableField content={sessionNote.id as any} />
        </Labeled>
        <Labeled marginLeft="1rem" label="Global Id">
          <CopiableField content={sessionNote.clientRecordingId} />
        </Labeled>
        <Labeled marginLeft="1rem" label="Status">
          <Typography>{sessionNote.status}</Typography>
        </Labeled>
        <Labeled marginLeft="1rem" label="Media Type">
          <Typography>{sessionNote.mediaType}</Typography>
        </Labeled>
        {sessionNote.mediaType === 'audio' && (
          <Labeled marginLeft="1rem" label="Files Count">
            <Typography>{sessionNote.filesCount}</Typography>
          </Labeled>
        )}
        <Labeled marginLeft="1rem" label="Aggregated Transcript">
          <Typography>
            {!!sessionNote?.transcript ? '✅ loaded' : '⚠️ missing'}
          </Typography>
        </Labeled>
        {!!sessionNote?.transcript && !!sessionNote?.wordCount && (
          <Labeled marginLeft="1rem" label="Word Count">
            <Typography>
              {sessionNote.wordCount?.transcript}
            </Typography>
          </Labeled>
        )}
        <Labeled marginLeft="1rem" label="Therapist Id">
          <CopiableField
            targetNavigationResource="therapists"
            targetNavigationFilters={{
              _id: sessionNote.therapistId,
              isHiddenTestAccount: 'visible',
            }}
            content={sessionNote.therapistId}
            filter={{
              therapistId: sessionNote.therapistId,
            }}
          />
        </Labeled>
        <Labeled marginLeft="1rem" label="Patient Id">
          <CopiableField
            targetNavigationResource="therapistPatients"
            targetNavigationFilters={{
              _id: { $oid: sessionNote.patientId },
            }}
            content={sessionNote.patientId}
          />
        </Labeled>
      </Box>
      {sessionNote.lastErrorMessage && (
        <Box
          display="flex"
          minWidth="30%"
          maxWidth="50%"
          marginLeft="1rem"
        >
          <Labeled label="Last Error Message" color="#f99">
            <Typography color="#f99">
              {sessionNote.lastErrorMessage}
            </Typography>
          </Labeled>
        </Box>
      )}
      {actionsAvailable && (
        <>
          <h3>Actions</h3>
          <section>
            {
              <Button onClick={onReloadRecordings}>
                {isLoading ? 'Loading' : 'Reload'} Recordings
              </Button>
            }
            {aggregateAvailable && (
              <ForceAggregationButton
                sessionNote={sessionNote}
                recordings={recordings || []}
                // clientRecordingId={sessionNote.clientRecordingId}
                // entryType={sessionNote.live ? "live-notes" : "session-notes"}
                // patientId={sessionNote.patientId}
                // therapistId={sessionNote.therapistId}
              />
            )}
            {forceProcessAvailable && (
              <Button
                disabled={isUpdating}
                onClick={onForceProcess}
                startIcon={
                  !isUpdating ? (
                    <CheckIcon />
                  ) : (
                    <CircularProgress size="1rem" />
                  )
                }
              >
                {!isUpdating ? sessionButtonText : 'Processing'}
              </Button>
            )}
          </section>
        </>
      )}

      {sessionNote.operationHistory?.length && (
        <>
          <h2>Operations</h2>
          <section>
            <RecordingOperationHistory
              key={sessionNote.id}
              operationHistory={sessionNote.operationHistory}
            />
          </section>
        </>
      )}

      <h2>Recordings</h2>
      <section>
        {isLoading && <CircularProgress size="1.5rem" />}
        {recordings
          ?.filter(rec => !!rec)
          .map(recording => (
            <TherapistRecordingView
              key={recording.id}
              recording={recording}
            />
          ))}
      </section>
    </SimpleShowLayout>
  );
};
